import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { getPagedSubscriptionPlans } from '@/services/subscription-plan.service';
import PageContent from '@/components/PageContent';
import { SubscriptionDurationList, SubscriptionPlan, SubscriptionTierList } from '@/models/subscription-plan.model';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const SubscriptionPlanListPage = () => {

  const [filterReq, setFilterReq] = useState<PaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: PaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<SubscriptionPlan> = await getPagedSubscriptionPlans(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching SubscriptionPlans:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/subscription/plans/new`)}>
          Add Plan
        </Button>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={1} align="first" sortable>
          <HeaderCell>Tier</HeaderCell>
          <Cell dataKey="tier">
            {(rowData: SubscriptionPlan) => SubscriptionTierList.findLast(i => i.value == rowData.tier)?.label ?? 'unknown'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="first" sortable>
          <HeaderCell>Duration</HeaderCell>
          <Cell dataKey="duration">
            {(rowData: SubscriptionPlan) => SubscriptionDurationList.findLast(i => i.value == rowData.duration)?.label ?? 'unknown'}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/subscription/plans/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider/>
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default SubscriptionPlanListPage;
